import React, { useEffect, useMemo, useState } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "../Pdf/documents/InvoicePDF.jsx";
import OrderDetails from "../Orders/OrderDetails";
import ErrorAlert from "../Alerts/ErrorAlert";
import LoadingAlert from "../Alerts/LoadingAlert";
import { BranchOfficeFetchAPI } from "../../../api/BranchOffices.jsx";
import { MachinesFetchAPI } from "../../../api/Machines.jsx";
import { PrinterFetchAPI } from "../../../api/Printer.jsx";
import { CategriesPosFetchAPI } from "../../../api/Paymentpost/CategoriesPos.jsx";
import { RenderIf } from "../utils/RenderIf";
import { buildCloudinaryImageUrl } from "../utils/CloudinaryImageUrl";

//Toast
import { toast } from "../../../utils/toast/toast.js";

const fmt = (n) => new Intl.NumberFormat("es-PA", { style: "currency", currency: "USD" }).format(Number(n) || 0);

const I = {
  Receipt: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="12" y2="14" /></svg>,
  X: () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  FileText: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
  Printer: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>,
  Card: () => <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>,
};

const safeNumber = (value) => Number(value) || 0;
const getType = (order) => {
  if (order?.is_devolution_notification || order?.order_type === "devolution" || order?.invoice_type === "devolution") return "Devolucion";
  if (order?.is_discount_notification || order?.discount > 0) return "Descuento";
  if (order?.order_type === "para llevar" || order?.order_type === "comer aqui") return "Venta";
  return "Transaccion";
};
const getTitle = (order, orderType) => {
  const type = getType(order);
  if (orderType === "temporal") return `Detalles de ${type}`;
  return type === "Transaccion" ? "Detalles de Transaccion" : `Detalles de ${type}`;
};
const getId = (order) => order?.transaction_code || order?.code || order?.order_id || order?._id || "-";
const getEvent = (order) => order?.event_name || "-";
const getMachineName = (order, machineInfo) => order?.machine_name || machineInfo?.machine_name || order?.machine_code || machineInfo?.code || "-";
const getUserName = (order) => order?.user?.name || order?.user_name || "No es cajera";
const getBranchName = (order) => order?.branchoffice?.name || order?.branchoffice?.branchoffice_name || "-";
const getClientName = (order) => order?.client_name || "-";
const getRuc = (order) => order?.client_ruc || "-";
const getDv = (order) => order?.client_dv || "-";
const getStandName = (order) => order?.stand_name || "-";
export default function TransactionDetailModal({
  orderData,
  orderType,
  businessData,
  temporalOrders,
  setTemporalOrdersData,
  setLoad,
  onClose,
}) {
  const token = localStorage.getItem("x-access-token");
  const logo = localStorage.getItem("logoBusiness") !== undefined && localStorage.getItem("logoBusiness") !== null && localStorage.getItem("logoBusiness") !== "null" && localStorage.getItem("logoBusiness") !== "undefined" ? localStorage.getItem("logoBusiness") : "";
  const [branchOfficeData, setBranchOfficeData] = useState({});
  const [machineInfo, setMachineInfo] = useState({});
  const [infoPrinter, setInfoPrinter] = useState({ printer_comanda: "", printer_ticket: "" });
  const [printers, setPrinters] = useState([]);

  const machine = useMemo(() => {
    try {
      const raw = localStorage.getItem("machine");
      return raw ? JSON.parse(raw) : { id: 0 };
    } catch {
      return { id: 0 };
    }
  }, []);

  const totals = useMemo(() => {
    const taxes = Array.isArray(orderData?.taxes) ? orderData.taxes.reduce((sum, tax) => sum + safeNumber(tax?.total), 0) : safeNumber(orderData?.tax);
    const delivery = safeNumber(orderData?.delivery?.value);
    return {
      discount: safeNumber(orderData?.discount),
      subtotal: safeNumber(orderData?.subtotal || orderData?.total),
      delivery,
      taxes,
      total: safeNumber(orderData?.total),
    };
  }, [orderData]);

  useEffect(() => {
    if (!orderData) return;

    const loadMeta = async () => {
      try {
        if (orderData?.business?.code && orderData?.branchoffice?.code) {
          const response = await BranchOfficeFetchAPI.getBranchOfficeByCode(orderData.business.code, orderData.branchoffice.code);
          setBranchOfficeData(response.data.branchoffice || {});
        }
      } catch {
        setBranchOfficeData({});
      }

      try {
        if (orderData?.machine_id) {
          const response = await MachinesFetchAPI.getmachineByid(orderData.machine_id, token);
          setMachineInfo(response.data.data || {});
        }
      } catch {
        setMachineInfo({});
      }

      try {
        const response = await PrinterFetchAPI.getPrinter(localStorage.getItem("x-access-token"), machine.id);
        const printerList = response.data.printers || [];
        const hydrated = [];
        let printerTicket = "";
        let printerComanda = "";

        for (const printer of printerList) {
          try {
            const categories = await CategriesPosFetchAPI.getCategoriesByPrinters(printer.printer_id, localStorage.getItem("x-access-machine-token"));
            printer.categories = categories.data.categories || [];
          } catch {
            printer.categories = [];
          }

          if (printer.printer_type === "Tickets") printerTicket = printer.printer_name;
          if (printer.printer_type === "Comanda") printerComanda = printer.printer_name;
          hydrated.push(printer);
        }

        setPrinters(hydrated);
        setInfoPrinter({ printer_comanda: printerComanda, printer_ticket: printerTicket });
      } catch {
        setPrinters([]);
      }
    };

    loadMeta();
  }, [machine.id, orderData, token]);

  if (!orderData) return null;

  const groups = Array.isArray(orderData.groups) ? orderData.groups : [];
  const combos = Array.isArray(orderData.combos) ? orderData.combos : [];
  const isApprovalRequest = orderType === "temporal";

  const printOrder = async () => {
    // LoadingAlert();
    const selectedPrinters = printers.filter((printer) => printer.printer_type === "Tickets");
    const payload = [{
      type_ticket: orderData.devolution === true ? "Nota a credito" : "factura",
      Printers: selectedPrinters,
      Nombre_maquina_impresora: infoPrinter.printer_ticket,
      Nombre_cocina_impresora: infoPrinter.printer_comanda,
      Nombre_zebra_impresora: "",
      Id_Orden_Imprimir: orderData._id,
      token: localStorage.getItem("x-access-machine-token"),
      transaction_code: orderData.code,
      amount_item: groups.length,
      imprimir_comanda: "no",
    }];

    try {
      const responseMachine = await MachinesFetchAPI.getmachineByid(machine.id, localStorage.getItem("x-access-machine-token"));
      await PrinterFetchAPI.printOrder(payload, responseMachine.data.data.windows_ip);
      // ErrorAlert("La orden se imprimio con exito.", "Orden impresa", "success");
      toast.confirm({
        type: "success",
        title: "La orden se imprimio con exito.",
        message: "Se imprimio correctamente, la factura de la orden.",
        confirmLabel: "Continuar",
        cancelLabel: null
      });
    } catch (err) {
      // console.log(err);
      // ErrorAlert("La orden no se imprimio con exito.  vfr xxx", "Error en la impresion", "error")

      //  toast.error({
      //    title: "Sin conexión",
      //    message: "Verifica tu internet e intenta de nuevo."
      //  });

       toast.confirm({
         type: "error",
         title: "La orden no se imprimio con exito.",
         message: "Verifica tu internet e intenta de nuevo o revisa la configuración de la impresora.",
         confirmLabel: "Ok",
         cancelLabel:  "Ok"
       });

      // if (reintentar);

    }
  };

  const invoiceDocument = (
    <InvoicePDF
      data={{
        emisor: orderData?.branchoffice?.fiscal_name,
        emisorRuc: orderData?.branchoffice?.ruc,
        emisorDv: orderData?.branchoffice?.dv,
        emisorDireccion: branchOfficeData?.direction,
        clienteNombre: orderData?.client_name,
        clienteRuc: orderData?.client_ruc,
        clienteDv: orderData?.client_dv,
        clienteDireccion: orderData?.client_address,
        clienteTelefono: orderData?.client_phone,
        fechaEmision: orderData?.createdAt ? new Date(orderData.createdAt).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "",
        puntoFacturacion: machineInfo?.code,
        consultaClaveUrl: orderData?.dgi_qr,
        cufe: orderData?.cufe,
        items: orderData?.groups || [],
        itbmsBreakdown: orderData?.taxes || [],
        metodoPago: "",
        formaPagoMonto: "",
        totalPagado: orderData?.total,
        tips: orderData?.tips,
        subtotal: orderData?.subtotal,
        discount: orderData?.discount,
        tax: orderData?.tax,
        total: orderData?.total,
        typeDocument: orderData?.order_type === "devolution" ? "credit_note" : "invoice",
        logo,
        aditional_info: orderData?.aditional_info || "",
        delivery: orderData?.delivery || { value: 0 },
      }}
    />
  );


  const money = (value) => `$ ${safeNumber(value).toFixed(2)}`;
  const itemImage = (url) => buildCloudinaryImageUrl({ url, width: 160, quality: "auto", type: "image" });
  const discountLabel = orderType === "permanent" || orderData?.is_devolution_notification ? "Descuento" : "Descuento unitario";
  const getDiscount = (item) => safeNumber(item?.discount ?? item?.unit_discount);
  const hasDiscount = (item) => getDiscount(item) > 0;

  const productMeta = (item) => (
    <>
      {Array.isArray(item.segments) && item.segments.map((segment) => (
        <div className="ts-modal__product-option" key={segment?._id || `${segment?.name}-${segment?.item?.name}`}>
          <span>{segment?.name}</span>
          <strong>{segment?.item?.name}</strong>
          <span>{money(segment?.item?.price)}</span>
        </div>
      ))}
      <div className="ts-modal__product-note">Entrega: {item.delivery_type || "-"}</div>
      <RenderIf isTrue={Boolean(item.note)}>
        <div className="ts-modal__product-note ts-modal__product-note--danger">Nota: {item.note}</div>
      </RenderIf>
    </>
  );

  const cardGroup = (product) => (
    <div className="ts-modal__product" key={product._id || product.group_id || product.name}>
      <img
        className="ts-modal__product-img"
        loading="lazy"
        src={itemImage(product.img)}
        alt={product.name || "Producto"}
      />
      <div className="ts-modal__product-info">
        <div className="ts-modal__product-head">
          <div className="ts-modal__product-name">{product.name}</div>
          <div className="ts-modal__product-summary">
            <span className="ts-modal__product-qty">x {product.amount || 1}</span>
            <span className="ts-modal__product-price" translate="no">{money(product.price)}</span>
          </div>
        </div>
        <RenderIf isTrue={hasDiscount(product)}>
          <div className="ts-modal__product-line">
            <span>{discountLabel}</span>
            <strong className="ts-modal__product-discount" translate="no">- {money(getDiscount(product))}</strong>
          </div>
        </RenderIf>
        {productMeta(product)}
      </div>
    </div>
  );

  const cardCombo = (combo) => (
    <div className="ts-modal__product" key={combo.combo_id || combo._id || combo.name}>
      <img
        className="ts-modal__product-img"
        loading="lazy"
        src={itemImage(combo.img)}
        alt={combo.name || "Combo"}
      />
      <div className="ts-modal__product-info">
        <div className="ts-modal__product-head">
          <div className="ts-modal__product-name">{combo.name}</div>
          <div className="ts-modal__product-summary">
            <span className="ts-modal__product-qty">x {combo.amount || 1}</span>
            <span className="ts-modal__product-price" translate="no">{money(combo.price)}</span>
          </div>
        </div>
        <div className="ts-modal__product-cat">Combo</div>
        <RenderIf isTrue={hasDiscount(combo)}>
          <div className="ts-modal__product-line">
            <span>{discountLabel}</span>
            <strong className="ts-modal__product-discount" translate="no">- {money(getDiscount(combo))}</strong>
          </div>
        </RenderIf>
        {Array.isArray(combo.setGroups) && combo.setGroups.map((setGroup) => (
          <div className="ts-modal__product-option" key={setGroup?.set_id || setGroup?.name}>
            <span>{setGroup?.name}</span>
            <strong>{Array.isArray(setGroup?.groups) ? setGroup.groups.map((group) => group?.name).filter(Boolean).join(", ") : ""}</strong>
          </div>
        ))}
        <div className="ts-modal__product-note">Entrega: {combo.delivery_type || "-"}</div>
        <RenderIf isTrue={Boolean(combo.note)}>
          <div className="ts-modal__product-note ts-modal__product-note--danger">Nota: {combo.note}</div>
        </RenderIf>
      </div>
    </div>
  );

  //We charge the items of the order 
  const orderItems = () => <>{groups.map(cardGroup)}{combos.map(cardCombo)}</>;

  return (
    <div className="ts-overlay" onClick={onClose}>
      <div className={`ts-modal ${isApprovalRequest ? "ts-modal--approval" : ""}`} onClick={(event) => event.stopPropagation()}>
        <div className="ts-modal__head">
          <div className="ts-modal__head-left">
            <div className="ts-modal__icon"><I.Receipt /></div>
            <div>
              <div className="ts-modal__title">{getTitle(orderData, orderType)}</div>
              <div className="ts-modal__id">{getId(orderData)}</div>
            </div>
          </div>
          <button className="ts-modal__close" onClick={onClose} type="button"><I.X /></button>
        </div>

        <div className={`ts-modal__body ${isApprovalRequest ? "ts-modal__body--approval" : ""}`}>
          {isApprovalRequest ? (
            <div className="ts-approval-panel">
              <OrderDetails
                temporalOrders={temporalOrders}
                setTemporalOrdersData={setTemporalOrdersData}
                orderData={{ ...orderData, taxes: orderData?.taxes || [], groups: orderData?.groups || [], combos: orderData?.combos || [], discounts: orderData?.discounts || [] }}
                order_type={orderType}
                business_data={businessData}
                setLoad={setLoad}
                setModalShow={(show) => { if (!show) onClose(); }}
              />
            </div>
          ) : (
            <>
              <div className="ts-modal__actions">
                {orderType === "permanent" ? (
                  <PDFDownloadLink className="ts-modal__action-btn" document={invoiceDocument}>
                    {({ loading }) => <>{<I.FileText />} {loading ? "Generando..." : orderData?.invoice_type === "devolution" ? "Descargar nota de credito" : "Descargar factura electronican"}</>}
                  </PDFDownloadLink>
                ) : null}
                <button className="ts-modal__action-btn ts-modal__action-btn--ghost" onClick={printOrder} type="button"><I.Printer /> Imprimir</button>
              </div>

              <div className="ts-modal__section-label">Informacion</div>
              <div className="ts-modal__info-grid">
                <div className="ts-modal__info-cell"><div className="ts-modal__info-label">Orden</div><div className="ts-modal__info-value">{getId(orderData)}</div></div>
                <div className="ts-modal__info-cell"><div className="ts-modal__info-label">Sucursal</div><div className="ts-modal__info-value">{getBranchName(orderData)}</div></div>
                <div className="ts-modal__info-cell"><div className="ts-modal__info-label">Usuario</div><div className="ts-modal__info-value">{getMachineName(orderData, machineInfo)} / {getUserName(orderData)}</div></div>
                <div className="ts-modal__info-cell"><div className="ts-modal__info-label">Maquina</div><div className="ts-modal__info-value">{machineInfo?.code || orderData?.machine_code || "-"}</div></div>
                <div className="ts-modal__info-cell"><div className="ts-modal__info-label">Cliente</div><div className="ts-modal__info-value">{getClientName(orderData)}</div></div>
                <div className="ts-modal__info-cell"><div className="ts-modal__info-label">Ruc/Dv</div><div className="ts-modal__info-value">{getRuc(orderData)} / {getDv(orderData)}</div></div>
              </div>

              <div className="ts-modal__status-row">
                <span className="ts-modal__status-label">Tipo</span>
                <span className={`ts-badge ${getType(orderData) === "Devolucion" ? "ts-badge--warning" : "ts-badge--blue"}`}>{getType(orderData)}</span>
                {/** Event name  */}
                <RenderIf isTrue={getEvent(orderData) !== "-"}>
                  <span className="ts-modal__status-label" style={{ marginLeft: 16 }}>Evento</span>
                  <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{getEvent(orderData)}</span>
                </RenderIf>
                {/**Stand name */}
                <RenderIf isTrue={getStandName(orderData) !== "-"}>
                  <span className="ts-modal__status-label" style={{ marginLeft: 16 }}>Stand</span>
                  <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{getStandName(orderData)}</span>
                </RenderIf>

              </div>
              {/**Productos */}
              <div className="ts-modal__section-label">Productos</div>
              <div className="ts-modal__products">
                {(groups.length + combos.length) > 0 ? orderItems() : (
                  <div className="ts-modal__products-empty">Sin productos registrados</div>
                )}
              </div>
              {/**Metodo de pago */}
              <div className="ts-modal__section-label">Metodo de pago</div>
              <div className="ts-modal__payment">
                <I.Card />
                <span className="ts-modal__payment-method">{orderData?.paymenthod || "Sin metodo"}</span>
                <span className="ts-modal__payment-amount">{fmt(totals.total)}</span>
              </div>
            </>
          )}
        </div>

        {!isApprovalRequest && <div className="ts-modal__footer">
          <div className="ts-modal__total-row"><span>Descuento:</span><span>{fmt(totals.discount)}</span></div>
          <div className="ts-modal__total-row"><span>Subtotal:</span><span>{fmt(totals.subtotal)}</span></div>
          <div className="ts-modal__total-row"><span>Envio:</span><span>{fmt(totals.delivery)}</span></div>
          <div className="ts-modal__total-row"><span>Impuesto:</span><span>{fmt(totals.taxes)}</span></div>
          <div className="ts-modal__total-row ts-modal__total-row--final"><span>Total:</span><span>{fmt(totals.total)}</span></div>
        </div>}
      </div>
    </div>
  );
}
